import React, { useCallback, useEffect, useState } from 'react';
import { Typography } from '@material-ui/core';
import { getRecurlyPublicKey, getRecurlyUrl } from '../../env/config_util';
import { FormattedMessage } from 'react-intl';
import messages from './messages';

const $script = require('scriptjs');

interface Props {
  brand: string;
  onPayPalSuccess: (token: any) => void;
  onPayPalError: (error: any) => void;
  onPayPalCancel: () => void;
  disabled?: boolean;
  userInfo: any;
}

declare var recurly: any;

const PayPalButton = ({ brand, onPayPalSuccess, onPayPalError, onPayPalCancel, disabled = false, userInfo }: Props) => {
  const [paypalReady, setPaypalReady] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const initializePayPal = useCallback(() => {
    try {
      const container = document.getElementById('paypal-container');
      if (container) {
        container.innerHTML = '';
      }
      // Use PayPal Complete format as per documentation
      const paypalConfig = {
        payPalComplete: {
          target: '#paypal-container',
          buttonOptions: {
            style: {
              layout: 'vertical',
              height: 44,
            },
          },
        },
      };

      const paypal = recurly.PayPal(paypalConfig);

      paypal.on('token', (token: any) => {
        onPayPalSuccess(token);
      });

      paypal.on('error', (error: any) => {
        console.error('PayPal error event:', error);
        console.error('PayPal error details:', JSON.stringify(error, null, 2));
        onPayPalError({
          message: `PayPal error: ${error.message || error.code || 'Unknown PayPal error'}`,
          originalError: error,
          errorCode: error.code,
          errorType: 'paypal_event_error',
        });
      });

      paypal.on('cancel', () => {
        onPayPalCancel();
      });
    } catch (error: any) {
      console.error('PayPal initialization error:', error);
      console.error('PayPal initialization error details:', JSON.stringify(error, null, 2));
      onPayPalError({
        message: `PayPal initialization error: ${error.message || 'Unknown error occurred'}`,
        originalError: error,
        errorType: 'paypal_init_error',
      });
    }
  }, [onPayPalError, onPayPalSuccess, onPayPalCancel]);

  useEffect(() => {
    let isMounted = true;

    const loadRecurlyPayPal = () => {
      $script(getRecurlyUrl(brand), () => {
        if (!isMounted) return;

        try {
          recurly.configure({
            publicKey: getRecurlyPublicKey(brand),
          });

          // Auto-initialize PayPal Complete when Recurly is ready
          initializePayPal();

          setPaypalReady(true);
          setInitializing(false);
        } catch (error: any) {
          console.error('Failed to initialize Recurly for PayPal:', error);
          console.error('Error details:', JSON.stringify(error, null, 2));
          onPayPalError({
            message: `Initialization failed: ${error.message || error}`,
            originalError: error,
          });
          setInitializing(false);
        }
      });
    };

    loadRecurlyPayPal();

    return () => {
      isMounted = false;
    };
  }, [brand, onPayPalError, initializePayPal]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}>
      {/* PayPal Complete will render the official PayPal button here */}
      <div
        id="paypal-container"
        style={{
          minHeight: '50px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: '600px',
          width: '100%',
        }}>
        {/* PayPal button will be injected here */}
      </div>

      {!paypalReady && !initializing && (
        <Typography variant="body2" style={{ marginTop: '8px', color: '#d32f2f' }}>
          <FormattedMessage {...messages.paypalNotAvailable} />
        </Typography>
      )}
    </div>
  );
};

export default PayPalButton;
